import { useState, useEffect } from 'react';
import { latestUpdateService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';

const LatestUpdateForm = ({ update, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    isActive: true,
    publishDate: new Date().toISOString().split('T')[0],
    displayOrder: 0
  });
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (update) {
      setFormData({
        title: update.title,
        link: update.link,
        isActive: update.isActive,
        publishDate: update.publishDate ? new Date(update.publishDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        displayOrder: update.displayOrder || 0
      });
    }
  }, [update]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPEG, PNG, etc.)');
        return;
      }
      setImage(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('isActive', formData.isActive);
      formDataToSend.append('publishDate', formData.publishDate);
      formDataToSend.append('displayOrder', formData.displayOrder);
      
      if (image) {
        formDataToSend.append('image', image);
      } else if (!update) {
        throw new Error('Image is required');
      }

      if (update) {
        await latestUpdateService.updateLatestUpdate(update._id, formDataToSend);
      } else {
        await latestUpdateService.createLatestUpdate(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save update. Please check all required fields and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="text-red-500">{error}</div>}
      
      {/* Show current image if editing */}
      {update && update.image && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Current Image</label>
          <img 
            src={getImageSrc(update.image)} 
            alt="Current update" 
            className="mt-2 h-32 object-cover rounded"
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Image *</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#166534]/90"
          required={!update}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Enter update title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Link *</label>
        <input
          type="url"
          name="link"
          value={formData.link}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="https://example.com"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          name="isActive"
          checked={formData.isActive}
          onChange={handleChange}
          className="h-4 w-4 text-[#166534] focus:ring-[#166534] border-gray-300 rounded"
        />
        <label className="ml-2 block text-sm text-gray-700">Active</label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Publish Date</label>
        <input
          type="date"
          name="publishDate"
          value={formData.publishDate}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Display Order</label>
        <input
          type="number"
          name="displayOrder"
          value={formData.displayOrder}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          placeholder="Lower numbers appear first"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166534] hover:bg-[#166534]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534] disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : update ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default LatestUpdateForm;